import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  IntelligentSearchState,
  IntelligentSearchRequest,
  IntelligentSearchResponse,
  ProcessedQuery,
  SearchIntent,
  IntelligentSuggestion,
  IntelligentSearchResult,
  PersonalizedCategory,
  SkillGapRecommendation,
  AdaptiveFilter,
  MasteryBasedFilter,
  SearchAnalytics,
  SearchBehaviorPattern,
  CategoryFeedback,
  SkillGapFeedback,
  MasteryFilterFeedback,
  AIError,
  MasteryState,
} from '@coffeeplexity/shared';
import type { RootState } from '../index';

const initialState: IntelligentSearchState = {
  // Natural language processing
  query: '',
  processedQuery: {
    originalQuery: '',
    normalizedQuery: '',
    extractedEntities: [],
    searchIntent: {
      primaryIntent: 'discover',
      secondaryIntents: [],
      confidence: 0,
      contextualFactors: [],
      userGoal: '',
    },
    confidence: 0,
    suggestedCorreections: [],
  },
  searchIntent: {
    primaryIntent: 'discover',
    secondaryIntents: [],
    confidence: 0,
    contextualFactors: [],
    userGoal: '',
  },
  suggestions: [],

  // AI-powered results
  results: [],
  personalizedCategories: [],
  skillGapRecommendations: [],

  // Adaptive filtering
  adaptiveFilters: [],
  masteryBasedFilters: [],
  difficultyRange: {
    min: 0,
    max: 10,
    optimal: 5,
    confidence: 0,
    reasoning: '',
  },

  // Search state
  loading: {
    search: false,
    suggestions: false,
    categories: false,
    skillGaps: false,
  },
  error: null,

  // Analytics and optimization
  searchAnalytics: {
    totalSearches: 0,
    successfulSearches: 0,
    averageResultsClicked: 0,
    averageTimeToClick: 0,
    queryComplexity: 0,
    intentAccuracy: 0,
    suggestionAcceptanceRate: 0,
    personalizationScore: 0,
    adaptiveFilterEffectiveness: 0,
    skillGapRelevance: 0,
    searchSatisfactionScore: 0,
    resultRelevanceScore: 0,
    discoverySuccessRate: 0,
    averageSearchLatency: 0,
    cacheHitRate: 0,
    errorRate: 0,
    lastUpdated: new Date().toISOString(),
  },
  userBehavior: [],

  // Cache and performance
  lastUpdated: null,
  cacheExpiry: null,
};

// Async thunks
export const performIntelligentSearch = createAsyncThunk(
  'intelligentSearch/performSearch',
  async (request: IntelligentSearchRequest, { rejectWithValue }) => {
    try {
      const startTime = Date.now();

      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/search/intelligent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const searchResponse: IntelligentSearchResponse = await response.json();
      const searchTime = Date.now() - startTime;

      return {
        ...searchResponse,
        searchTime,
      };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'INTELLIGENT_SEARCH_ERROR',
        message: error.message || 'Intelligent search failed',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchIntelligentSuggestions = createAsyncThunk(
  'intelligentSearch/fetchSuggestions',
  async (
    {
      query,
      userId,
      context,
      maxSuggestions = 5,
    }: {
      query: string;
      userId: string;
      context?: Record<string, any>;
      maxSuggestions?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        query,
        userId,
        maxSuggestions: maxSuggestions.toString(),
        ...(context && { context: JSON.stringify(context) }),
      });

      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/search/suggestions?${params}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch suggestions: ${response.statusText}`);
      }

      const suggestions: IntelligentSuggestion[] = await response.json();
      return suggestions;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'SUGGESTIONS_FETCH_ERROR',
        message: error.message || 'Failed to fetch intelligent suggestions',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchPersonalizedCategories = createAsyncThunk(
  'intelligentSearch/fetchCategories',
  async (
    {
      userId,
      masteryStates,
      maxCategories = 8,
    }: {
      userId: string;
      masteryStates: MasteryState[];
      maxCategories?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/search/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          masteryStates,
          maxCategories,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.statusText}`);
      }

      const categories: PersonalizedCategory[] = await response.json();
      return categories;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'CATEGORIES_FETCH_ERROR',
        message: error.message || 'Failed to fetch personalized categories',
        recoverable: true,
        fallbackStrategy: 'curated_content',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchSkillGapRecommendations = createAsyncThunk(
  'intelligentSearch/fetchSkillGaps',
  async (
    {
      userId,
      masteryStates,
      maxRecommendations = 5,
    }: {
      userId: string;
      masteryStates: MasteryState[];
      maxRecommendations?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/search/skill-gaps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          masteryStates,
          maxRecommendations,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch skill gaps: ${response.statusText}`);
      }

      const recommendations: SkillGapRecommendation[] = await response.json();
      return recommendations;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'SKILL_GAPS_FETCH_ERROR',
        message: error.message || 'Failed to fetch skill gap recommendations',
        recoverable: true,
        fallbackStrategy: 'curated_content',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const updateAdaptiveFilters = createAsyncThunk(
  'intelligentSearch/updateFilters',
  async (
    {
      userId,
      masteryStates,
      userPreferences,
    }: {
      userId: string;
      masteryStates: MasteryState[];
      userPreferences?: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/search/adaptive-filters', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          masteryStates,
          userPreferences,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update filters: ${response.statusText}`);
      }

      const filters: AdaptiveFilter[] = await response.json();
      return filters;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'FILTERS_UPDATE_ERROR',
        message: error.message || 'Failed to update adaptive filters',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const submitCategoryFeedback = createAsyncThunk(
  'intelligentSearch/submitCategoryFeedback',
  async (
    {
      categoryId,
      feedback,
      userId,
    }: {
      categoryId: string;
      feedback: CategoryFeedback;
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/search/category-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryId,
          feedback,
          userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit feedback: ${response.statusText}`);
      }

      return { categoryId, feedback };
    } catch (error: any) {
      const aiError: AIError = {
        code: 'CATEGORY_FEEDBACK_ERROR',
        message: error.message || 'Failed to submit category feedback',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

const intelligentSearchSlice = createSlice({
  name: 'intelligentSearch',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    clearQuery: state => {
      state.query = '';
      state.processedQuery = initialState.processedQuery;
      state.searchIntent = initialState.searchIntent;
    },
    clearResults: state => {
      state.results = [];
      state.suggestions = [];
    },
    clearError: state => {
      state.error = null;
    },
    updateSearchIntent: (state, action: PayloadAction<SearchIntent>) => {
      state.searchIntent = action.payload;
    },
    markSuggestionUsed: (state, action: PayloadAction<string>) => {
      const suggestion = state.suggestions.find(s => s.id === action.payload);
      if (suggestion) {
        suggestion.metadata = {
          ...suggestion.metadata,
          used: true,
          usedAt: new Date().toISOString(),
        };
      }
    },
    markResultClicked: (state, action: PayloadAction<string>) => {
      const result = state.results.find(r => r.id === action.payload);
      if (result) {
        result.metadata = {
          ...result.metadata,
          clicked: true,
          clickedAt: new Date().toISOString(),
        };
      }
    },
    updateDifficultyRange: (
      state,
      action: PayloadAction<{ min: number; max: number; optimal: number }>
    ) => {
      state.difficultyRange = {
        ...state.difficultyRange,
        ...action.payload,
      };
    },
    addBehaviorPattern: (
      state,
      action: PayloadAction<SearchBehaviorPattern>
    ) => {
      state.userBehavior.unshift(action.payload);
      // Keep only the most recent 50 patterns
      if (state.userBehavior.length > 50) {
        state.userBehavior = state.userBehavior.slice(0, 50);
      }
    },
    updateSearchAnalytics: (
      state,
      action: PayloadAction<Partial<SearchAnalytics>>
    ) => {
      state.searchAnalytics = {
        ...state.searchAnalytics,
        ...action.payload,
        lastUpdated: new Date().toISOString(),
      };
    },
    setCacheExpiry: (state, action: PayloadAction<string>) => {
      state.cacheExpiry = action.payload;
    },
  },
  extraReducers: builder => {
    // Perform Intelligent Search
    builder
      .addCase(performIntelligentSearch.pending, state => {
        state.loading.search = true;
        state.error = null;
      })
      .addCase(performIntelligentSearch.fulfilled, (state, action) => {
        state.loading.search = false;
        state.results = action.payload.results;
        state.personalizedCategories = action.payload.personalizedCategories;
        state.skillGapRecommendations = action.payload.skillGapRecommendations;
        state.adaptiveFilters = action.payload.adaptiveFilters;
        state.suggestions = action.payload.suggestions;
        state.processedQuery = action.payload.processedQuery;
        state.searchIntent = action.payload.searchIntent;
        state.searchAnalytics = {
          ...state.searchAnalytics,
          ...action.payload.analytics,
          averageSearchLatency: action.payload.searchTime,
        };
        state.lastUpdated = new Date().toISOString();
        state.cacheExpiry = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 minutes
      })
      .addCase(performIntelligentSearch.rejected, (state, action) => {
        state.loading.search = false;
        state.error = (action.payload as AIError)?.message || 'Search failed';
      });

    // Fetch Intelligent Suggestions
    builder
      .addCase(fetchIntelligentSuggestions.pending, state => {
        state.loading.suggestions = true;
      })
      .addCase(fetchIntelligentSuggestions.fulfilled, (state, action) => {
        state.loading.suggestions = false;
        state.suggestions = action.payload;
      })
      .addCase(fetchIntelligentSuggestions.rejected, (state, action) => {
        state.loading.suggestions = false;
        state.error =
          (action.payload as AIError)?.message || 'Failed to fetch suggestions';
      });

    // Fetch Personalized Categories
    builder
      .addCase(fetchPersonalizedCategories.pending, state => {
        state.loading.categories = true;
      })
      .addCase(fetchPersonalizedCategories.fulfilled, (state, action) => {
        state.loading.categories = false;
        state.personalizedCategories = action.payload;
      })
      .addCase(fetchPersonalizedCategories.rejected, (state, action) => {
        state.loading.categories = false;
        state.error =
          (action.payload as AIError)?.message || 'Failed to fetch categories';
      });

    // Fetch Skill Gap Recommendations
    builder
      .addCase(fetchSkillGapRecommendations.pending, state => {
        state.loading.skillGaps = true;
      })
      .addCase(fetchSkillGapRecommendations.fulfilled, (state, action) => {
        state.loading.skillGaps = false;
        state.skillGapRecommendations = action.payload;
      })
      .addCase(fetchSkillGapRecommendations.rejected, (state, action) => {
        state.loading.skillGaps = false;
        state.error =
          (action.payload as AIError)?.message || 'Failed to fetch skill gaps';
      });

    // Update Adaptive Filters
    builder.addCase(updateAdaptiveFilters.fulfilled, (state, action) => {
      state.adaptiveFilters = action.payload;
    });

    // Submit Category Feedback
    builder.addCase(submitCategoryFeedback.fulfilled, (state, action) => {
      const { categoryId, feedback } = action.payload;
      const category = state.personalizedCategories.find(
        c => c.id === categoryId
      );
      if (category) {
        category.metadata.userFeedback = feedback;
      }
    });
  },
});

export const {
  setQuery,
  clearQuery,
  clearResults,
  clearError,
  updateSearchIntent,
  markSuggestionUsed,
  markResultClicked,
  updateDifficultyRange,
  addBehaviorPattern,
  updateSearchAnalytics,
  setCacheExpiry,
} = intelligentSearchSlice.actions;

// Selectors
export const selectIntelligentSearchQuery = (state: RootState) =>
  state.intelligentSearch.query;
export const selectProcessedQuery = (state: RootState) =>
  state.intelligentSearch.processedQuery;
export const selectSearchIntent = (state: RootState) =>
  state.intelligentSearch.searchIntent;
export const selectIntelligentSuggestions = (state: RootState) =>
  state.intelligentSearch.suggestions;
export const selectIntelligentSearchResults = (state: RootState) =>
  state.intelligentSearch.results;
export const selectPersonalizedCategories = (state: RootState) =>
  state.intelligentSearch.personalizedCategories;
export const selectSkillGapRecommendations = (state: RootState) =>
  state.intelligentSearch.skillGapRecommendations;
export const selectAdaptiveFilters = (state: RootState) =>
  state.intelligentSearch.adaptiveFilters;
export const selectMasteryBasedFilters = (state: RootState) =>
  state.intelligentSearch.masteryBasedFilters;
export const selectDifficultyRange = (state: RootState) =>
  state.intelligentSearch.difficultyRange;
export const selectIntelligentSearchLoading = (state: RootState) =>
  state.intelligentSearch.loading;
export const selectIntelligentSearchError = (state: RootState) =>
  state.intelligentSearch.error;
export const selectSearchAnalytics = (state: RootState) =>
  state.intelligentSearch.searchAnalytics;
export const selectUserBehaviorPatterns = (state: RootState) =>
  state.intelligentSearch.userBehavior;

// Memoized selectors
export const selectHighConfidenceSuggestions = createSelector(
  [selectIntelligentSuggestions],
  suggestions => suggestions.filter(s => s.confidence >= 0.7)
);

export const selectPersonalizedSuggestions = createSelector(
  [selectIntelligentSuggestions],
  suggestions =>
    suggestions.filter(
      s => s.type === 'personalized' || s.type === 'skill_based'
    )
);

export const selectTopPersonalizedCategories = createSelector(
  [selectPersonalizedCategories],
  categories =>
    categories.sort((a, b) => b.relevanceScore - a.relevanceScore).slice(0, 6)
);

export const selectHighPrioritySkillGaps = createSelector(
  [selectSkillGapRecommendations],
  recommendations => recommendations.filter(r => r.priority === 'high')
);

export const selectActiveAdaptiveFilters = createSelector(
  [selectAdaptiveFilters],
  filters => filters.filter(f => f.isActive)
);

export const selectResultsByRelevance = createSelector(
  [selectIntelligentSearchResults],
  results => [...results].sort((a, b) => b.relevanceScore - a.relevanceScore)
);

export const selectResultsByPersonalization = createSelector(
  [selectIntelligentSearchResults],
  results =>
    [...results].sort((a, b) => b.personalizedScore - a.personalizedScore)
);

export const selectSearchPerformanceMetrics = createSelector(
  [selectSearchAnalytics],
  analytics => ({
    averageLatency: analytics.averageSearchLatency,
    successRate:
      analytics.successfulSearches / Math.max(analytics.totalSearches, 1),
    cacheHitRate: analytics.cacheHitRate,
    errorRate: analytics.errorRate,
    userSatisfaction: analytics.searchSatisfactionScore,
  })
);

export const selectRecentBehaviorPatterns = createSelector(
  [selectUserBehaviorPatterns],
  patterns => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return patterns.filter(p => new Date(p.lastObserved) > oneWeekAgo);
  }
);

export const selectIsSearchCacheStale = createSelector(
  [
    selectIntelligentSearchResults,
    (state: RootState) => state.intelligentSearch.cacheExpiry,
  ],
  (results, cacheExpiry) => {
    if (results.length === 0 || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export default intelligentSearchSlice.reducer;
